import { vsName as projectCostDetailComponent } from "./views/info/component";
import { vsComponentName as projectDetailMachineComponentName } from "./views/info/machines";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../records/grids/columns/columnDefinitionService";
import { vsObjectName as projectName } from "./shared/configs/project";
import {
  ProjectCostServiceProvider,
  vsProviderName as projectCostServiceProviderName,
} from "./shared/ProjectCostService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../records/server/serverUtility";
import { Record } from "../records/server/crud";
import {
  GridConfiguration,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../records/views/recordGridView";

import { vsObjectName as machineName } from "./../machines/sanmanuel/configs/machine";
import { vsObjectName as projectMachineName } from "./shared/configs/projectMachines";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../records/grids/settings/gridSettingsService";

import {
  vsComponentName as configurableGridFormViewComponentName,
  ConfigurableGridFormViewConfig,
} from "../records/views/configurableGridFormView";

import { ServiceResponse } from "../records/server/query";
import {
  AuthorizedUiElementConfig,
  AuthorizedUiElementServiceProvider,
} from "../ui/authorizedUiElement";
import { Machine } from "../machines/shared/models/machine";
import { MachineConfiguration } from "../machines/shared/models/machineConfiguration";
import { vsProviderName as machineDetailTabsServiceProviderName } from "../machines/shared/components/views/machines/detail/tabs";

const sensitiveInfoPermission = "projectSensitiveInfo";

registerProjects.$inject = [
  projectCostServiceProviderName,
  machineDetailTabsServiceProviderName,
];
export function registerProjects(
  projectServiceProvider: ProjectCostServiceProvider,
  machineDetailTabs: AuthorizedUiElementServiceProvider<AuthorizedUiElementConfig>
) {
  machineDetailTabs.addElement({
    displayName: "Projects",
    id: "projects",
    uiSrefFragment: "projects",
    requiredPermission: "projectView",
    stateDefinition: {
      url: "/projects",
      component: configurableGridFormViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          (recordGridViewService: RecordGridViewService): GridConfiguration => {
            const gridConfiguration =
              recordGridViewService.getGridConfiguration(projectMachineName)!;
            gridConfiguration.nameSingular = "Project";
            gridConfiguration.namePlural = "Projects";

            return gridConfiguration;
          },
        ],

        columns: [
          "gridConfiguration",
          "columnDefinitionService",
          (
            gridConfiguration: GridConfiguration,
            columnDefinitionService: ColumnDefinitionService
          ) => [
            // Add base action columns
            columnDefinitionService.getLinkColDef({
              colId: "info",
              headerName: "Info",
              iconClass: "fa fa-info-circle text-primary",
              stateExpression: "main.project.info.machines({id: data.Id})",
            }),

            ...(gridConfiguration.columns ?? []),
          ],
        ],
        config: [
          "gridConfiguration",
          "machine",
          (
            gridConfiguration: GridConfiguration,
            machine: Machine<MachineConfiguration>
          ): ConfigurableGridFormViewConfig => {
            return {
              defaultQuery: { MachineId: machine.Id },
              gridConfiguration,
              additionalToggleButtons: [],
              showNavButton: false,
              showSelectButtons: false,
            };
          },
        ],
      },
    },
  });

  projectServiceProvider.addProjectCostType({
    id: projectName,
    name: "Project Machines",
    detailComponentName: projectCostDetailComponent,
    formConfigName: projectName,
    gridConfigName: projectName,
    sensitiveInfoPermission,
    tabs: [
      {
        name: "Machines",
        uiSref: "machines",
        state: {
          url: "/machines",
          views: {
            tabs: {
              component: projectDetailMachineComponentName,
            },
          },
          resolve: {
            defaultQuery: [
              "project",
              (project: any) => {
                return {
                  ProjectId: project.Id,
                };
              },
            ],
          },
        },
        children: [
          {
            name: "Edit",
            uiSref: "edit",
            state: {
              url: "/edit",
              views: {
                "tabs@main.project.type.info": {
                  component: configurableGridFormViewComponentName,
                },
              },
              resolve: {
                selectedIds: [
                  RecordGridViewServiceName,
                  ServerUtilityName,
                  "project",
                  (
                    recordGridViewService: RecordGridViewService,
                    serverUtility: ServerUtility,
                    project: Record
                  ) => {
                    const projectMachineConfig =
                      recordGridViewService.getGridConfiguration(
                        projectMachineName
                      )!;
                    return serverUtility
                      .fetchAll<Record>({
                        url: projectMachineConfig.gridDataSource!.url,
                        query: {
                          ProjectId: project.Id,
                        },
                      })
                      .then((result) => result.map((r) => r.MachineId));
                  },
                ],
                gridConfiguration: [
                  RecordGridViewServiceName,
                  GridSettingsServiceName,
                  (
                    recordGridViewService: RecordGridViewService,
                    gridSettingsService: GridSettingsService
                  ) => {
                    const gridConfiguration =
                      recordGridViewService.getGridConfiguration(machineName)!;
                    gridConfiguration.gridSettings =
                      gridSettingsService.getGridSettings(
                        Symbol("projectMachines.edit")
                      );
                    return gridConfiguration;
                  },
                ],
                config: [],
              },
            },
          },
        ],
      },
    ],
  });
}

function setProjectMachine(
  serverUtility: ServerUtility,
  machineId: number,
  projectId: number,
  setSelection: boolean
) {
  const url = "/api/project/project/machines";

  setSelection
    ? serverUtility.post({
        url,
        postObject: {
          ProjectId: projectId,
          MachineId: machineId,
        },
      })
    : serverUtility.sendApiRequest<ServiceResponse<Record>>({
        url: url + `/${projectId}/byMachineId`,
        method: "delete",
        data: {
          MachineId: machineId,
        },
      });
}
